import type * as THREE from 'three'
import type { PlayerController, PlayerCollisionTelemetry } from '../player'
import type { DebugMaterialRegistry } from '../debug/debug-materials'

export interface PhysicsTickEvent {
  dt: number
  player: PlayerController
  telemetry: PlayerCollisionTelemetry
}

export interface RenderTickEvent {
  alpha: number
  player: PlayerController
  camera: THREE.Camera
}

export interface EngineEvents {
  physicsTick: (event: PhysicsTickEvent) => void
  renderTick: (event: RenderTickEvent) => void
  levelLoaded: (levelId: string) => void
}

export class EngineEventBus {
  private readonly listeners = new Map<keyof EngineEvents, Set<Function>>()

  on<K extends keyof EngineEvents>(event: K, handler: EngineEvents[K]): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)!.add(handler)
    return () => this.off(event, handler)
  }

  off<K extends keyof EngineEvents>(event: K, handler: EngineEvents[K]): void {
    this.listeners.get(event)?.delete(handler)
  }

  emit<K extends keyof EngineEvents>(event: K, ...args: Parameters<EngineEvents[K]>): void {
    const handlers = this.listeners.get(event)
    if (handlers) {
      for (const fn of handlers) {
        fn(...args)
      }
    }
  }

  clear(): void {
    this.listeners.clear()
  }
}

export interface EnginePlugin {
  id: string
  install(bus: EngineEventBus, context: EngineContext): void
  uninstall?(): void
}

export interface EngineContext {
  scene: THREE.Scene
  camera: THREE.PerspectiveCamera
  renderer: THREE.WebGLRenderer
  player: PlayerController
  debugRegistry?: DebugMaterialRegistry
}
